import React, { useEffect, useState } from 'react';
import classNames from 'classnames';
import { obliczWartosciKredytu } from './helpers/calculate';
import { LinkTarget } from 'atomic/link/link-target.enum';
import { SeoLinkRelEnum } from 'modules/seo-module/interfaces/seo-link-rel.enum';
import { roundToNearest } from 'helpers/number';
import WidgetDSATitle from 'modules/shared/widgets/components/widget-dsa/components/widget-dsa-title/widget-dsa-title';
import veloLogoSVG from 'assets/images/logo-velo.svg';
import Tooltip from 'atomic/tooltip/tooltip';
import RangeSlider from 'atomic/range-slider';
import Input from 'atomic/form/input';
import Icon from 'atomic/icon';
import Link from 'atomic/link';
import Image from 'atomic/image';
import {
    FINANCING_MIN_VALUE,
    FINANCING_MAX_VALUE,
    FINANCING_STEP_VALUE,
    PERIOD_INITIAL_VALUE,
    PERIOD_MIN_VALUE,
    PERIOD_MAX_VALUE,
    isFinancingAllowed,
    isFinancingWithinBounds,
    veloDSA
} from './helpers/financing';

import './product-financing.scss';

const PRIMARY_COLOR = '#00b140';
const SECONDARY_COLOR = '#e8e8e8';
const FINANCING_FORM_INPUT_TIMEOUT = 1300;

interface ProductFinancingProps {
    productPrice: number;
    isMobile: boolean;
}

const ProductFinancing: React.FC<ProductFinancingProps> = ({
    productPrice,
    isMobile
}) => {
    const financingInitialPrice = roundToNearest(productPrice, FINANCING_STEP_VALUE);

    const [financingPrice, setFinancingPrice] = useState<number | string>(financingInitialPrice);
    const [financingPeriod, setFinancingPeriod] = useState<number | string>(PERIOD_INITIAL_VALUE);
    const [result, setResult] = useState<number | string>(obliczWartosciKredytu(financingInitialPrice, PERIOD_INITIAL_VALUE, 0).rataKredytu);

    const calculateResult = (price: number, period: number): void => {
        if (isFinancingWithinBounds(price, period)) {
            const financingValues = obliczWartosciKredytu(price, period, 0);

            setResult(financingValues.rataKredytu);
        } else {
            setResult('-');
        }
    };

    const validateFinancingPrice = (value: number): number => {
        if (value < FINANCING_MIN_VALUE) {
            return FINANCING_MIN_VALUE;
        } else if (value > FINANCING_MAX_VALUE) {
            return FINANCING_MAX_VALUE;
        } else {
            return roundToNearest(value, FINANCING_STEP_VALUE);
        }
    };

    const validateFinancingPeriod = (value: number): number => {
        if (value < PERIOD_MIN_VALUE) {
            return PERIOD_MIN_VALUE;
        } else if (value > PERIOD_MAX_VALUE) {
            return PERIOD_MAX_VALUE;
        } else {
            return value;
        }
    };

    useEffect(() => {
        const timeOutId = setTimeout(() => {
            const validatedPrice = validateFinancingPrice(Number(financingPrice));
            const validatedPeriod = validateFinancingPeriod(Number(financingPeriod));

            if (validatedPrice !== Number(financingPrice)) {
                setFinancingPrice(validatedPrice);
            }

            if (validatedPeriod !== Number(financingPeriod)) {
                setFinancingPeriod(validatedPeriod);
            }

            calculateResult(validatedPrice, validatedPeriod);
        }, FINANCING_FORM_INPUT_TIMEOUT);

        return () => clearTimeout(timeOutId);
    }, [financingPrice, financingPeriod]);

    const onPriceChange = (value: number): void => {
        if (isNaN(value)) {
            return;
        }

        setFinancingPrice(value);

        calculateResult(value, Number(financingPeriod));
    };

    const onPriceInputChange = (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>): void => {
        if (event.target.value !== '') {
            const value = Number(event.target.value);

            setFinancingPrice(value);
        } else {
            setFinancingPrice('');
        }
    };

    const onPriceInputBlur = (event: { target: unknown; type?: unknown; }): void => {
        let value = 0;

        if ((event.target as HTMLInputElement)?.value !== '') {
            value = Number((event.target as HTMLInputElement)?.value);
        }

        if (value < FINANCING_MIN_VALUE) {
            setFinancingPrice(FINANCING_MIN_VALUE);
            calculateResult(FINANCING_MIN_VALUE, Number(financingPeriod));
        } else if (value > FINANCING_MAX_VALUE) {
            setFinancingPrice(FINANCING_MAX_VALUE);
            calculateResult(FINANCING_MAX_VALUE, Number(financingPeriod));
        } else {
            value = roundToNearest(value, FINANCING_STEP_VALUE);

            setFinancingPrice(value);
            calculateResult(value, Number(financingPeriod));
        }
    };

    const onPeriodChange = (value: number): void => {
        if (isNaN(value)) {
            return;
        }

        setFinancingPeriod(value);

        calculateResult(Number(financingPrice), value);
    };

    const onPeriodInputChange = (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>): void => {
        if (event.target.value !== '') {
            const value = Number(event.target.value);

            setFinancingPeriod(value);
        } else {
            setFinancingPeriod('');
        }
    };

    const onPeriodInputBlur = (event: { target: unknown; type?: unknown; }): void => {
        let value = 0;

        if ((event.target as HTMLInputElement)?.value !== '') {
            value = Number((event.target as HTMLInputElement)?.value);
        }

        if (value < PERIOD_MIN_VALUE) {
            setFinancingPeriod(PERIOD_MIN_VALUE);
            calculateResult(Number(financingPrice), PERIOD_MIN_VALUE);
        } else if (value > PERIOD_MAX_VALUE) {
            setFinancingPeriod(PERIOD_MAX_VALUE);
            calculateResult(Number(financingPrice), PERIOD_MAX_VALUE);
        } else {
            setFinancingPeriod(value);
            calculateResult(Number(financingPrice), value);
        }
    };

    const renderStatistics = (): React.ReactNode => {
        return (
            <>
                <div className="product-financing__result">
                    <div className="rate-title">
                        Rata
                    </div>
                    <div className="rate-value">
                        {result} PLN
                    </div>
                </div>
                <div className="product-financing__rrso">
                    RRSO 9,9%
                    <Link
                        type="button"
                        color="primary"
                        size="lg"
                        noWrap={true}
                        href="https://csr.onet.pl/1746213/trk/adclick/das,1811664,0?CS=tracking"
                        target={LinkTarget.BLANK}
                        rel={SeoLinkRelEnum.NOFOLLOW_NOOPENER_NOREFERRER}
                        ariaLabel="Sprawdź ratę"
                    >
                        Sprawdź ratę <Icon name="arrow-right-circle" color="white" size={20} />
                    </Link>
                </div>
            </>
        );
    };

    if (!isFinancingAllowed(productPrice)) {
        return null;
    }

    return (
        <div className="product-financing">
            <div className="row">
                <div
                    className={classNames('col', {
                        'col-6': !isMobile,
                        'col-12': isMobile
                    })}
                >
                    <div className="product-financing__dsa">
                        <WidgetDSATitle dsa={veloDSA} />
                    </div>
                    <div className="product-financing__header">
                        <Image
                            src={veloLogoSVG}
                            alt="VELO Bank"
                            title="VELO Bank"
                            width={48}
                            height={55}
                            show={true}
                            loading="lazy"
                        />
                        <div className="product-financing__header-title">
                            <div className="financing-type">Pożyczka gotówkowa</div>
                            <div className="financing-info">Pierwsza rata nawet za 3 miesiące</div>
                        </div>
                    </div>
                    {!isMobile && renderStatistics()}
                </div>
                <div
                    className={classNames('col', {
                        'col-6': !isMobile,
                        'col-12': isMobile
                    })}
                >
                    <div className="product-financing__value">
                        <Input
                            name="financingPrice"
                            type="number"
                            min={FINANCING_MIN_VALUE}
                            max={FINANCING_MAX_VALUE}
                            step={FINANCING_STEP_VALUE}
                            label="Zł."
                            small={false}
                            value={financingPrice}
                            onChange={onPriceInputChange}
                            onBlur={onPriceInputBlur}
                        />
                    </div>
                    <div className="product-financing__slider">
                        <RangeSlider
                            min={FINANCING_MIN_VALUE}
                            max={FINANCING_MAX_VALUE}
                            step={FINANCING_STEP_VALUE}
                            defaultValue={Number(financingPrice)}
                            primaryColor={PRIMARY_COLOR}
                            secondaryColor={SECONDARY_COLOR}
                            onChange={onPriceChange}
                        />
                    </div>
                    <div className="product-financing__value">
                        <Input
                            name="financingPeriod"
                            type="number"
                            min={PERIOD_MIN_VALUE}
                            max={PERIOD_MAX_VALUE}
                            label="mies"
                            small={false}
                            value={financingPeriod}
                            onChange={onPeriodInputChange}
                            onBlur={onPeriodInputBlur}
                        />
                    </div>
                    <div className="product-financing__slider">
                        <RangeSlider
                            min={PERIOD_MIN_VALUE}
                            max={PERIOD_MAX_VALUE}
                            defaultValue={Number(financingPeriod)}
                            primaryColor={PRIMARY_COLOR}
                            secondaryColor={SECONDARY_COLOR}
                            onChange={onPeriodChange}
                        />
                    </div>
                    {isMobile && renderStatistics()}
                </div>
            </div>
            <div className="row">
                <div className="col col-12">
                    <div className="product-financing__tooltip">
                        Informacje o kalkulatorze i koszcie kredytu
                        <Tooltip
                            trigger={<span className="product-financing__tooltip-icon">
                                <Icon
                                    className="icon-info"
                                    name="info"
                                    color="primary"
                                    size={20}
                                    tabIndex={0}
                                    aria-label="Otwórz info"
                                    role="button"
                                />
                            </span>}
                            title={<>Informacje o kalkulatorze i koszcie kredytu</>}
                        >
                            <div className="tooltip-text">
                                <p>
                                    Wyliczenia kalkulatora mają charakter szacunkowy i nie stanowią oferty kredytowej
                                    w rozumieniu art. 66 § 1 Kodeksu Cywilnego.
                                </p>
                                <p>
                                    Rzeczywista Roczna Stopa Oprocentowania (RRSO) wynosi 9,9%, całkowita kwota pożyczki
                                    (bez kredytowanych kosztów) 62 200 zł, całkowita kwota do zapłaty 80 304,86 zł, oprocentowanie
                                    zmienne 9,46%, całkowity koszt pożyczki 18 104,86 zł (w tym: prowizja 0 zł, odsetki 18 104,86 zł,
                                    opłata za prowadzenie VeloKonta oraz obsługę karty do Konta w okresie trwania pożyczki 0 zł),
                                    67 miesięcznych rat równych w wysokości po 1 198,58 zł każda. Kalkulację wykonaliśmy 05.03.2025 r.
                                    na reprezentatywnym przykładzie.
                                </p>
                                <p>
                                    Z oferty pożyczki gotówkowej może skorzystać Klient, jeśli: w ciągu ostatnich 6 miesięcy nie
                                    miał kredytu/pożyczki gotówkowej ani kredytu konsolidacyjnego w VeloBank S.A., ma aktywne zgody
                                    marketingowe (w tym sms, mejl i telefon) oraz zadeklaruje, że w całym okresie pożyczki spełni
                                    warunek oferty, tj. będzie mieć VeloKonto i co miesiąc: zapewni wpływ na konto min. 2000 zł
                                    oraz wykona min. 5 płatności kartą do konta lub BLIKIEM. Jeżeli nie spełni warunku oferty,
                                    od kolejnego miesiąca podwyższymy oprocentowanie pożyczki o 2 pp. - do czasu ponownego
                                    spełnienia warunku.
                                </p>
                                <p>
                                    Udzielenie, wysokość pożyczki oraz warunki pożyczki zależą od wyniku badania i oceny Twojej
                                    zdolności kredytowej oraz oceny ryzyka kredytowego. Maksymalna kwota pożyczki w wysokości
                                    300 tysięcy zł dostępna jest dla dwóch pożyczkobiorców i uzależniona jest od oceny ryzyka kredytowego.
                                    Podczas wnioskowania o pożyczkę gotówkową możesz skorzystać z karencji w spłacie pierwszej raty
                                    od 1 do 3 miesięcy i wówczas warunki kosztowe pożyczki ulegną zmianie. W okresie karencji nie
                                    spłacasz kapitału ani odsetek. Termin płatności raty kapitałowo-odsetkowej będzie zapisany w umowie.
                                </p>
                                <p>
                                    Prowadzenie VeloKonta to prowadzenie rachunku płatniczego. Szczegółowe warunki oferty, w tym
                                    opłaty i prowizje, znajdziesz w placówkach oraz na velobank.pl.
                                </p>
                            </div>
                        </Tooltip>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ProductFinancing;
